import React from 'react'
import { Segmented } from 'antd'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react-lite'
import { useStores } from '../../cls/stores/MobXProvider'
import { useSmartTranslation } from '../../cls/TranslationContext'

/**
 * ProgressInput component renders a range time picker.
 * Allows users to select the progress of an item.
 *
 * @param {Object} props - The component props.
 * @param {string} props.value - The current progress value of the item
 * @param {function} props.onChange - The callback function to handle value changes.
 * @returns {JSX.Element} The rendered Segmented component.
 */
const ProgressInput = ({
	value,
	onChange,
	type = 'sprint',
	viewOnly = false,
	...props
}) => {
	const { phaseTwoLimited, ...rest } = props
	const { users } = useStores()
	const { loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	//TODO: transform isAdmin check into has team permission check when implementing team ecosystem
	const { t } = useSmartTranslation()
	const stepCanceled = {
		label: t('single.canceled'),
		value: 'canceled'
	}

	const phase_one = [
		{
			label: t('single.draft'),
			value: 'draft',
			disabled: value === 'in_review' && !isAdmin
		}
	]

	if (type === 'sprint') {
		phase_one.push({
			label: t('single.in-review'),
			value: 'in_review'
		})

		// if in_review, and isAdmin, add a new option: "aprove" with value active
		if (value === 'in_review' && isAdmin) {
			phase_one.push({
				label: t('single.approved'),
				value: 'active'
			})
		}
	}

	const phase_two = [
		stepCanceled,
		{
			label: t('single.active'),
			value: 'active'
			// disabled: !isAdmin
		}
	]
	if (!phaseTwoLimited) {
		phase_two.push({
			label: t('single.pending'),
			value: 'pending'
			// disabled: !isAdmin
		})
		phase_two.push({
			label: t('single.completed'),
			value: 'completed'
			// disabled: !isAdmin
		})
	}

	const phase_three = [...phase_one, stepCanceled]

	const in_phase_one = value === 'draft' || value === 'in_review'
	const in_phase_three = value === 'canceled'

	// Determine options based on the type
	let options
	if (type == 'monthly-report') {
		options = [
			stepCanceled,
			{
				label: t('single.draft'),
				value: 'draft'
			},
			{
				label: t('single.pending'),
				value: 'pending'
			},
			{
				label: t('single.completed'),
				value: 'completed'
			}
		]
	} else if (type == 'request') {
		options = [
			{
				label: t('single.read'),
				value: 'completed'
			},
			{
				label: t('single.not-read'),
				value: 'not_read'
			}
		]
	} else if (type == 'notification') {
		options = [
			{
				label: t('single.read'),
				value: 'read'
			},
			{
				label: t('single.not-read'),
				value: 'not_read'
			}
		]
	} else if (type == 'recruitment') {
		options = [
			{
				label: t('single.pending'),
				value: 'pending'
			},
			{
				label: t('single.reviewed'),
				value: 'reviewed'
			},
			{
				label: t('single.approved'),
				value: 'approved'
			},
			{
				label: t('single.rejected'),
				value: 'rejected'
			}
		]
	} else {
		options = in_phase_one
			? phase_one
			: !in_phase_three
			? phase_two
			: phase_three
	}

	return (
		<>
			{viewOnly ? (
				<span
					style={{
						textTransform: 'capitalize'
					}}
				>
					{value}
				</span>
			) : (
				<Segmented
					value={value}
					onChange={onChange}
					style={{
						border: '1px solid #d9d9d9'
					}}
					options={options}
					{...rest}
				/>
			)}
		</>
	)
}

/**
 * Prop types for the ProgressInput component.
 */
ProgressInput.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func,
	phaseTwoLimited: PropTypes.bool,
	type: PropTypes.string,
	viewOnly: PropTypes.bool
}

export default observer(ProgressInput)
